// apps/web/src/pages/onboarding/[token]/address.tsx — Phase 7 plan 07-06.
//
// Onboarding step 1: address. The page is a thin wrapper around
// WizardShell + a form with 3 fields (street, postalCode, city).
// On submit it calls `Mutation.saveOnboardingAddress`; on success
// it navigates to the capacity step. Per D-07-12 the validation
// is hybrid: a per-field check blocks the next-click BEFORE the
// network call, then the server re-validates with the same schema.
//
// The web bundle intentionally avoids the `zod` runtime to keep
// the bundle slim — the wizard uses hand-rolled validation
// (apps/web/src/wizard/validation.ts). The server-side
// `OnboardingAddressInput` Zod check is the source of truth; this
// client-side mirror keeps the UX fast but is not authoritative.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { WizardShell } from "../../../components/WizardShell";
import { gqlRequest } from "../../../lib/api";

const SAVE_ADDRESS_MUTATION = /* GraphQL */ `
  mutation SaveOnboardingAddress($input: OnboardingAddressInput!) {
    saveOnboardingAddress(input: $input) {
      onboardingProgress
      restaurant {
        id
        slug
      }
    }
  }
`;

interface AddressInput {
  addressLine: string;
  postalCode: string;
  city: string;
}

type AddressErrors = Partial<Record<keyof AddressInput, boolean>>;

function validateAddress(input: AddressInput): AddressErrors {
  const errs: AddressErrors = {};
  if (input.addressLine.trim().length === 0 || input.addressLine.length > 200) {
    errs.addressLine = true;
  }
  if (!/^\d{4,10}$/.test(input.postalCode)) errs.postalCode = true;
  if (input.city.trim().length === 0 || input.city.length > 100) {
    errs.city = true;
  }
  return errs;
}

export function OnboardingAddressPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState<AddressInput>({
    addressLine: "",
    postalCode: "",
    city: "",
  });
  const [errors, setErrors] = useState<AddressErrors>({});

  const mutation = useMutation({
    mutationFn: () => gqlRequest(SAVE_ADDRESS_MUTATION, { input: form }),
    onSuccess: () => {
      navigate(`/onboarding/${token}/capacity`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = validateAddress(form);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  return (
    <WizardShell currentStep="address">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <h1 className="font-heading text-2xl">
          {t("onboarding.addressTitle")}
        </h1>
        <label className="flex flex-col gap-1">
          <span className="font-heading text-sm">
            {t("onboarding.addressStreet")}
          </span>
          <input
            type="text"
            value={form.addressLine}
            onChange={(e) =>
              setForm((s: AddressInput) => ({
                ...s,
                addressLine: e.target.value,
              }))
            }
            className="field"
            data-testid="onboarding-address-input"
          />
          {errors.addressLine ? (
            <span className="text-[11px] text-red-700" role="alert">
              {t("onboarding.requiredField")}
            </span>
          ) : null}
        </label>
        <div className="flex gap-2">
          <label className="flex flex-col gap-1 flex-1">
            <span className="font-heading text-sm">
              {t("onboarding.addressPostalCode")}
            </span>
            <input
              type="text"
              value={form.postalCode}
              onChange={(e) =>
                setForm((s: AddressInput) => ({
                  ...s,
                  postalCode: e.target.value,
                }))
              }
              className="field"
              data-testid="onboarding-postal-input"
              autoComplete="postal-code"
            />
            {errors.postalCode ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t("onboarding.requiredField")}
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1 flex-1">
            <span className="font-heading text-sm">
              {t("onboarding.addressCity")}
            </span>
            <input
              type="text"
              value={form.city}
              onChange={(e) =>
                setForm((s: AddressInput) => ({ ...s, city: e.target.value }))
              }
              className="field"
              data-testid="onboarding-city-input"
              autoComplete="address-level2"
            />
            {errors.city ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t("onboarding.requiredField")}
              </span>
            ) : null}
          </label>
        </div>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary"
          data-testid="onboarding-address-submit"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" aria-hidden />
              {t("onboarding.submitting")}
            </span>
          ) : (
            t("onboarding.saveNext")
          )}
        </button>
      </form>
    </WizardShell>
  );
}
